import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import FileTreeNavigation from '../../components/ui/FileTreeNavigation';
import DownloadActionPanel from '../../components/ui/DownloadActionPanel';
import CodePreview from './components/CodePreview';
import SearchBar from './components/SearchBar';
import ProjectStats from './components/ProjectStats';
import MobileViewToggle from './components/MobileViewToggle';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import projectService from '../../services/projectService';
import fileService from '../../services/fileService';
import VersionHistoryTimeline from '../project-dashboard/components/VersionHistoryTimeline';
import { GitBranch } from 'lucide-react';

const FileExplorer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileView, setMobileView] = useState('tree');
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [projectStructure, setProjectStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentProject, setCurrentProject] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/authentication');
      return;
    }
    loadProjectData();
  }, [user, navigate]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      const projects = await projectService?.getAll(user?.id);
      
      if (projects?.length > 0) {
        const project = projects?.[0];
        setCurrentProject(project);
        
        const files = await fileService?.getFilesByProject(project?.id);
        const tree = fileService?.buildFileTree(files);
        setProjectStructure(tree);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFile = async (file, newContent) => {
    try {
      await fileService?.updateFile(file?.id, {
        content: newContent,
        fileSize: new Blob([newContent])?.size
      });
      
      // Reload project data to reflect changes
      await loadProjectData();
    } catch (err) {
      console.error('Failed to save file:', err);
      throw err;
    }
  };

  const mockProjectStructure = [
    {
      name: 'giati-ecommerce-app',
      type: 'folder',
      path: '/giati-ecommerce-app',
      children: [
        {
          name: 'README.md',
          type: 'file',
          path: '/giati-ecommerce-app/README.md',
          size: 2048,
          content: `# Giati E-Commerce Application\n\nA modern, full-stack e-commerce platform built with React, Node.js, and PostgreSQL.\n\n## Features\n\n- User authentication and authorization\n- Product catalog with search and filtering\n- Shopping cart functionality\n- Secure payment processing\n- Order management system\n- Admin dashboard\n- Responsive design\n\n## Tech Stack\n\n**Frontend:**\n- React 18.2.0\n- React Router 6.0.2\n- Tailwind CSS 3.x\n- Axios for API calls\n\n**Backend:**\n- Node.js with Express\n- PostgreSQL database\n- JWT authentication\n- Stripe payment integration\n\n## Getting Started\n\n### Prerequisites\n- Node.js 16+\n- PostgreSQL 14+\n- npm or yarn\n\n### Installation\n\n1. Clone the repository\n2. Install dependencies: npm install\n3. Set up environment variables\n4. Run database migrations\n5. Start development server: npm run dev\n\n## Project Structure\n\nSee folder structure for detailed organization.\n\n## License\n\nMIT License - see LICENSE file for details.`
        },
        {
          name: 'frontend',
          type: 'folder',
          path: '/giati-ecommerce-app/frontend',
          children: [
            {
              name: 'src',
              type: 'folder',
              path: '/giati-ecommerce-app/frontend/src',
              children: [
                {
                  name: 'App.jsx',
                  type: 'file',
                  path: '/giati-ecommerce-app/frontend/src/App.jsx',
                  size: 1536,
                  content: `import React from 'react'
;\nimport { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
;\nimport Header from './components/Header'
;\nimport Footer from '../landing-page/components/Footer'
;\nimport HomePage from './pages/HomePage'
;\nimport ProductPage from './pages/ProductPage'
;\nimport CartPage from './pages/CartPage'
;\nimport CheckoutPage from './pages/CheckoutPage'
;\nimport ProfilePage from './pages/ProfilePage'
;\nimport AdminDashboard from './pages/AdminDashboard'
;\n\nfunction App() {\n  return (\n    <Router>\n      <div className="min-h-screen flex flex-col bg-background">\n        <Header />\n        <main className="flex-1">\n          <Routes>\n            <Route path="/" element={<HomePage />} />\n            <Route path="/products/:id" element={<ProductPage />} />\n            <Route path="/cart" element={<CartPage />} />\n            <Route path="/checkout" element={<CheckoutPage />} />\n            <Route path="/profile" element={<ProfilePage />} />\n            <Route path="/admin" element={<AdminDashboard />} />\n          </Routes>\n        </main>\n        <Footer />\n      </div>\n    </Router>\n  );\n}\n\nexport default App;`
                },
                {
                  name: 'components',
                  type: 'folder',
                  path: '/giati-ecommerce-app/frontend/src/components',
                  children: [
                    {
                      name: 'Header.jsx',
                      type: 'file',
                      path: '/giati-ecommerce-app/frontend/src/components/Header.jsx',
                      size: 2048,
                      content: `import React, { useState } from 'react'
;\nimport { Link } from 'react-router-dom'
;\nimport { ShoppingCart, User, Menu, X } from 'lucide-react'
;\n\nconst Header = () => {\n  const [isMenuOpen, setIsMenuOpen] = useState(false);\n  const cartItemCount = 3;\n\n  return (\n    <header className="bg-card border-b border-border sticky top-0 z-50">\n      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">\n        <div className="flex items-center justify-between h-16">\n          <Link to="/" className="flex items-center gap-2">\n            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">\n              <span className="text-white font-bold text-xl">G</span>\n            </div>\n            <span className="text-xl font-semibold">Giati Store</span>\n          </Link>\n\n          <nav className="hidden md:flex items-center gap-6">\n            <Link to="/" className="hover:text-primary transition-colors">Home</Link>\n            <Link to="/products" className="hover:text-primary transition-colors">Products</Link>\n            <Link to="/about" className="hover:text-primary transition-colors">About</Link>\n            <Link to="/contact" className="hover:text-primary transition-colors">Contact</Link>\n          </nav>\n\n          <div className="flex items-center gap-4">\n            <Link to="/cart" className="relative p-2 hover:bg-muted rounded-md transition-colors">\n              <ShoppingCart size={24} />\n              {cartItemCount > 0 && (\n                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">\n                  {cartItemCount}\n                </span>\n              )}\n            </Link>\n            <Link to="/profile" className="p-2 hover:bg-muted rounded-md transition-colors">\n              <User size={24} />\n            </Link>\n            <button\n              onClick={() => setIsMenuOpen(!isMenuOpen)}\n              className="md:hidden p-2 hover:bg-muted rounded-md transition-colors"\n            >\n              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}\n            </button>\n          </div>\n        </div>\n      </div>\n\n      {isMenuOpen && (\n        <div className="md:hidden border-t border-border bg-card">\n          <nav className="flex flex-col p-4 gap-2">\n            <Link to="/" className="p-3 hover:bg-muted rounded-md transition-colors">Home</Link>\n            <Link to="/products" className="p-3 hover:bg-muted rounded-md transition-colors">Products</Link>\n            <Link to="/about" className="p-3 hover:bg-muted rounded-md transition-colors">About</Link>\n            <Link to="/contact" className="p-3 hover:bg-muted rounded-md transition-colors">Contact</Link>\n          </nav>\n        </div>\n      )}\n    </header>\n  );\n};\n\nexport default Header;`
                    },
                    {
                      name: 'ProductCard.jsx',
                      type: 'file',
                      path: '/giati-ecommerce-app/frontend/src/components/ProductCard.jsx',
                      size: 1792,
                      content: `import React from 'react'
;\nimport { Link } from 'react-router-dom'
;\nimport { ShoppingCart, Star } from 'lucide-react'
;\n\nconst ProductCard = ({ product }) => {\n  return (\n    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">\n      <Link to={\`/products/\${product.id}\`}>\n        <div className="aspect-square overflow-hidden bg-muted">\n          <img\n            src={product.image}\n            alt={product.imageAlt}\n            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"\n          />\n        </div>\n      </Link>\n      <div className="p-4">\n        <Link to={\`/products/\${product.id}\`}>\n          <h3 className="font-semibold text-lg mb-2 hover:text-primary transition-colors line-clamp-2">\n            {product.name}\n          </h3>\n        </Link>\n        <div className="flex items-center gap-2 mb-3">\n          <div className="flex items-center gap-1">\n            {Array.from({ length: 5 }).map((_, i) => (\n              <Star\n                key={i}\n                size={16}\n                className={i < product.rating ? 'fill-warning text-warning' : 'text-muted-foreground'}\n              />\n            ))}\n          </div>\n          <span className="text-sm text-muted-foreground">({product.reviews})</span>\n        </div>\n        <div className="flex items-center justify-between">\n          <div>\n            <span className="text-2xl font-bold text-primary">\${product.price}</span>\n            {product.originalPrice && (\n              <span className="text-sm text-muted-foreground line-through ml-2">\n                \${product.originalPrice}\n              </span>\n            )}\n          </div>\n          <button className="p-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors">\n            <ShoppingCart size={20} />\n          </button>\n        </div>\n      </div>\n    </div>\n  );\n};\n\nexport default ProductCard;`
                    }
                  ]
                },
                {
                  name: 'pages',
                  type: 'folder',
                  path: '/giati-ecommerce-app/frontend/src/pages',
                  children: [
                    {
                      name: 'HomePage.jsx',
                      type: 'file',
                      path: '/giati-ecommerce-app/frontend/src/pages/HomePage.jsx',
                      size: 1024,
                      content: `import React from 'react'
;\nimport ProductCard from '../components/ProductCard'
;\n\nconst HomePage = () => {\n  const featuredProducts = [\n    {\n      id: 1,\n      name: 'Premium Wireless Headphones',\n      price: 299.99,\n      originalPrice: 399.99,\n      rating: 5,\n      reviews: 128,\n      image: 'https://images.pexels.com/photos/3394650/pexels-photo-3394650.jpeg',\n  imageAlt: 'Black wireless over-ear headphones with cushioned ear cups on white background'\n  },\n  {\n  id: 2,\n  name: 'Smart Watch Pro',\n  price: 449.99,\n  rating: 4,\n  reviews: 89,\n  image: 'https://images.pexels.com/photos/437037/pexels-photo-437037.jpeg',\n      imageAlt: 'Silver smartwatch with black band displaying fitness metrics on screen'\n    }\n  ];\n\n  return (\n    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">\n      <section className="mb-12">\n        <h1 className="text-4xl font-bold mb-4">Welcome to Giati Store</h1>\n        <p className="text-lg text-muted-foreground">Discover amazing products at great prices</p>\n      </section>\n\n      <section>\n        <h2 className="text-2xl font-semibold mb-6">Featured Products</h2>\n        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">\n          {featuredProducts.map(product => (\n            <ProductCard key={product.id} product={product} />\n          ))}\n        </div>\n      </section>\n    </div>\n  );\n};\n\nexport default HomePage;`
                    }
                  ]
                }
              ]
            },
            {
              name: 'package.json',
              type: 'file',
              path: '/giati-ecommerce-app/frontend/package.json',
              size: 1024,
              content: `{\n  "name": "giati-ecommerce-frontend",\n  "version": "1.0.0",\n  "type": "module",\n  "scripts": {\n    "dev": "vite",\n    "build": "vite build",\n    "preview": "vite preview"\n  },\n  "dependencies": {\n    "react": "^18.2.0",\n    "react-dom": "^18.2.0",\n    "react-router-dom": "^6.0.2",\n    "axios": "^1.8.4",\n    "lucide-react": "^0.484.0"\n  },\n  "devDependencies": {\n    "@vitejs/plugin-react": "^4.0.0",\n    "vite": "^4.3.9",\n    "tailwindcss": "^3.3.0",\n    "autoprefixer": "^10.4.14",\n    "postcss": "^8.4.24"\n  }\n}`
            }
          ]
        },
        {
          name: 'backend',
          type: 'folder',
          path: '/giati-ecommerce-app/backend',
          children: [
            {
              name: 'server.js',
              type: 'file',
              path: '/giati-ecommerce-app/backend/server.js',
              size: 2560,
              content: `const express = require('express');\nconst cors = require('cors');\nconst helmet = require('helmet');\nconst morgan = require('morgan');\nconst dotenv = require('dotenv');\nconst { Pool } = require('pg');\n\ndotenv.config();\n\nconst app = express();\nconst PORT = process.env.PORT || 5000;\n\n// Database connection\nconst pool = new Pool({\n  connectionString: process.env.DATABASE_URL,\n  ssl: process.env.NODE_ENV === 'production' ? { rejectUnauthorized: false } : false\n});\n\n// Middleware\napp.use(helmet());\napp.use(cors());\napp.use(express.json());\napp.use(morgan('dev'));\n\n// Routes\napp.use('/api/auth', require('./routes/auth'));\napp.use('/api/products', require('./routes/products'));\napp.use('/api/orders', require('./routes/orders'));\napp.use('/api/users', require('./routes/users'));\n\n// Health check\napp.get('/health', (req, res) => {\n  res.json({ status: 'ok', timestamp: new Date().toISOString() });\n});\n\n// Error handling middleware\napp.use((err, req, res, next) => {\n  console.error(err.stack);\n  res.status(err.status || 500).json({\n    error: {\n      message: err.message || 'Internal server error',\n      status: err.status || 500\n    }\n  });\n});\n\n// Start server\napp.listen(PORT, () => {\n  console.log(\`Server running on port \${PORT}\`);\n});\n\nmodule.exports = app;`
            },
            {
              name: 'routes',
              type: 'folder',
              path: '/giati-ecommerce-app/backend/routes',
              children: [
                {
                  name: 'products.js',
                  type: 'file',
                  path: '/giati-ecommerce-app/backend/routes/products.js',
                  size: 1792,
                  content: `const express = require('express');\nconst router = express.Router();\nconst { Pool } = require('pg');\nconst pool = new Pool();\n\n// Get all products\nrouter.get('/', async (req, res) => {\n  try {\n    const { category, minPrice, maxPrice, search } = req.query;\n    let query = 'SELECT * FROM products WHERE 1=1';\n    const params = [];\n\n    if (category) {\n      params.push(category);\n      query += \` AND category = $\${params.length}\`;\n    }\n\n    if (minPrice) {\n      params.push(minPrice);\n      query += \` AND price >= $\${params.length}\`;\n    }\n\n    if (maxPrice) {\n      params.push(maxPrice);\n      query += \` AND price <= $\${params.length}\`;\n    }\n\n    if (search) {\n      params.push(\`%\${search}%\`);\n      query += \` AND (name ILIKE $\${params.length} OR description ILIKE $\${params.length})\`;\n    }\n\n    query += ' ORDER BY created_at DESC';\n\n    const result = await pool.query(query, params);\n    res.json(result.rows);\n  } catch (error) {\n    console.error('Error fetching products:', error);\n    res.status(500).json({ error: 'Failed to fetch products' });\n  }\n});\n\n// Get single product\nrouter.get('/:id', async (req, res) => {\n  try {\n    const { id } = req.params;\n    const result = await pool.query('SELECT * FROM products WHERE id = $1', [id]);\n    \n    if (result.rows.length === 0) {\n      return res.status(404).json({ error: 'Product not found' });\n    }\n\n    res.json(result.rows[0]);\n  } catch (error) {\n    console.error('Error fetching product:', error);\n    res.status(500).json({ error: 'Failed to fetch product' });\n  }\n});\n\nmodule.exports = router;`
                }
              ]
            }
          ]
        },
        {
          name: 'database',
          type: 'folder',
          path: '/giati-ecommerce-app/database',
          children: [
            {
              name: 'schema.sql',
              type: 'file',
              path: '/giati-ecommerce-app/database/schema.sql',
              size: 3072,
              content: `-- Users table\nCREATE TABLE users (\n  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),\n  email VARCHAR(255) UNIQUE NOT NULL,\n  password_hash VARCHAR(255) NOT NULL,\n  first_name VARCHAR(100),\n  last_name VARCHAR(100),\n  role VARCHAR(20) DEFAULT 'customer',\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,\n  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);\n\n-- Products table\nCREATE TABLE products (\n  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),\n  name VARCHAR(255) NOT NULL,\n  description TEXT,\n  price DECIMAL(10, 2) NOT NULL,\n  category VARCHAR(100),\n  stock_quantity INTEGER DEFAULT 0,\n  image_url TEXT,\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,\n  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);\n\n-- Orders table\nCREATE TABLE orders (\n  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),\n  user_id UUID REFERENCES users(id),\n  total_amount DECIMAL(10, 2) NOT NULL,\n  status VARCHAR(50) DEFAULT 'pending',\n  shipping_address TEXT,\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,\n  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);\n\n-- Order items table\nCREATE TABLE order_items (\n  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),\n  order_id UUID REFERENCES orders(id),\n  product_id UUID REFERENCES products(id),\n  quantity INTEGER NOT NULL,\n  price DECIMAL(10, 2) NOT NULL,\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);\n\n-- Create indexes\nCREATE INDEX idx_users_email ON users(email);\nCREATE INDEX idx_products_category ON products(category);\nCREATE INDEX idx_orders_user_id ON orders(user_id);\nCREATE INDEX idx_order_items_order_id ON order_items(order_id);`
            },
            {
              name: 'migrations',
              type: 'folder',
              path: '/giati-ecommerce-app/database/migrations',
              children: [
                {
                  name: '001_initial_schema.sql',
                  type: 'file',
                  path: '/giati-ecommerce-app/database/migrations/001_initial_schema.sql',
                  size: 512,
                  content: `-- Migration: Initial Schema\n-- Created: 2026-01-08\n\nBEGIN;\n\n-- Create users table\nCREATE TABLE IF NOT EXISTS users (\n  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),\n  email VARCHAR(255) UNIQUE NOT NULL,\n  password_hash VARCHAR(255) NOT NULL,\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);\n\nCOMMIT;`
                }
              ]
            }
          ]
        },
        {
          name: '.env.example',
          type: 'file',
          path: '/giati-ecommerce-app/.env.example',
          size: 256,
          content: `# Database\nDATABASE_URL=postgresql://user:password@localhost:5432/giati_ecommerce\n\n# JWT\nJWT_SECRET=your-secret-key-here\nJWT_EXPIRES_IN=7d\n\n# Server\nPORT=5000\nNODE_ENV=development\n\n# Stripe\nSTRIPE_SECRET_KEY=sk_test_your_key_here\nSTRIPE_PUBLISHABLE_KEY=pk_test_your_key_here`
        }
      ]
    }
  ];

  const projectStats = {
    totalFiles: currentProject?.fileCount || 0,
    totalFolders: 12,
    projectSize: currentProject?.projectSize || '0 MB',
    linesOfCode: 3847
  };

  const countFilteredItems = (items, query) => {
    if (!query) return 0;
    let count = 0;
    
    items?.forEach(item => {
      if (item?.name?.toLowerCase()?.includes(query?.toLowerCase())) {
        count++;
      }
      if (item?.type === 'folder' && item?.children) {
        count += countFilteredItems(item?.children, query);
      }
    });
    
    return count;
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    if (window.innerWidth < 1024) {
      setMobileView('preview');
    }
  };

  const handleCopyCode = (file) => {
    console.log('Code copied:', file?.name);
  };

  const handleDownloadFile = (file) => {
    const blob = new Blob([file.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file?.name;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadProject = () => {
    console.log('Downloading complete project...');
    setTimeout(() => {
      setShowDownloadPanel(false);
      navigate('/project-dashboard');
    }, 3000);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileView('tree');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resultCount = countFilteredItems(projectStructure, searchQuery);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading project...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-destructive mb-4">Error: {error}</p>
            <Button onClick={loadProjectData}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation />
      <div className="content-wrapper">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">Project Explorer</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Browse and preview your generated project files
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => navigate('/code-generation')}
                >
                  Back
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="GitBranch"
                    iconPosition="left"
                    onClick={() => setShowVersionHistory(true)}
                  >
                    Version History
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => setShowDownloadPanel(true)}
                  >
                    Download
                  </Button>
                </div>
              </div>
            </div>

            <ProjectStats stats={projectStats} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
            <div className={`lg:col-span-3 ${mobileView === 'tree' ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4 md:p-6 border-b border-border bg-surface-elevated">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary flex items-center justify-center">
                      <Icon name="FolderTree" size={20} color="#FFFFFF" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-base md:text-lg">File Tree</h2>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {projectStats?.totalFiles} files
                      </p>
                    </div>
                  </div>
                  <SearchBar
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    resultCount={resultCount}
                  />
                </div>
                <div className="h-[500px] md:h-[600px] lg:h-[700px]">
                  <FileTreeNavigation
                    projectStructure={projectStructure}
                    selectedFile={selectedFile?.path}
                    onFileSelect={handleFileSelect}
                  />
                </div>
              </div>
            </div>

            <div className={`lg:col-span-9 ${mobileView === 'preview' ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-card border border-border rounded-lg overflow-hidden h-[500px] md:h-[600px] lg:h-[700px]">
                <CodePreview
                  file={selectedFile}
                  onCopyCode={handleCopyCode}
                  onDownloadFile={handleDownloadFile}
                  onSaveFile={handleSaveFile}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <MobileViewToggle
        activeView={mobileView}
        onViewChange={setMobileView}
      />
      {showDownloadPanel && (
        <DownloadActionPanel
          projectName="Giati E-Commerce App"
          projectSize={projectStats?.projectSize}
          fileCount={projectStats?.totalFiles}
          onDownload={handleDownloadProject}
          onClose={() => setShowDownloadPanel(false)}
          isVisible={showDownloadPanel}
        />
      )}

      {showVersionHistory && currentProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Version History: {currentProject?.name}
              </h2>
              <button
                onClick={() => setShowVersionHistory(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <VersionHistoryTimeline
                projectId={currentProject?.id}
                onRestore={() => {
                  loadProjectData();
                  setShowVersionHistory(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileExplorer;